import { useCallback, useEffect, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box } from "@mui/material"
import type { DateTime } from "luxon"

import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../../modules/store/storeHooks"
import { selectEffectiveUserId } from "../../../../../modules/context/contextSelectors"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { ProductDetailsDialog } from "../shop/product/ProductDetailsDialog"
import { useTakeawayCart } from "./TakeawayCartContext"
import type { TakeawayCartItem, TakeawayKitchen, TakeawayProduct } from "./takeawayTypes"
import { FAVORITES_GROUP_ID } from "./takeawayTypes"
import { fetchTakeawayMenu, fetchUserTakeawayProducts, TakeawayQuery } from "./takeawayApi"
import { useApiImageUrl } from "./useApiImageUrl"
import { useTakeawayScrollGroups } from "./useTakeawayScrollGroups"
import { TakeawayDateBar } from "./components/TakeawayDateBar"
import { TakeawayCategoryBar } from "./components/TakeawayCategoryBar"
import { TakeawayFormSkeleton } from "./components/TakeawayFormSkeleton"
import { TakeawayHero } from "./components/TakeawayHero"
import { TakeawayMenuContent } from "./components/TakeawayMenuContent"

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
    stickyHeader: {
        position: "sticky",
        top: 0,
        zIndex: 20,
    },
    content: {
        padding: "1.5rem",
        flex: 1,
        background: theme.palette.tailwind.slate[100],
    },
}))

type TakeawayPageProps = Readonly<{
    kitchen: TakeawayKitchen
    selectedDate: DateTime
    cart: TakeawayCartItem[]
    onDateChange: (date: DateTime) => void
    onQuantityChange: (product: TakeawayProduct, quantity: number) => void
    onFavoriteToggle: (product: TakeawayProduct, makeFavorite: boolean) => Promise<boolean>
}>

export function TakeawayPage({
    kitchen,
    selectedDate,
    cart,
    onDateChange,
    onQuantityChange,
    onFavoriteToggle,
}: TakeawayPageProps) {
    const { classes } = useStyles()
    const dt = useDateTime()
    const userId = useAppSelector(selectEffectiveUserId)
    const cartContext = useTakeawayCart()

    const [detailsProduct, setDetailsProduct] = useState<TakeawayProduct | null>(null)
    const [showFavorites, setShowFavorites] = useState(false)
    const [search, setSearch] = useState("")
    const heroImageUrl = useApiImageUrl(kitchen.brandingDetails?.imageUrl)
    const dateString = dt.toDate(selectedDate)

    const onBehalfUserId = cartContext?.onBehalfUser?.userId
    const webshopId = kitchen.webshop?.id
    const detailsUserId = onBehalfUserId ?? userId

    const defaultMenuQuery = useApiQuery(fetchTakeawayMenu, {
        queryName: TakeawayQuery.TAKEAWAY_MENU,
        dependencies: { kitchenId: kitchen.id, date: dateString },
        enabled: !onBehalfUserId,
        keepPreviousData: true,
    })

    const userProductsQuery = useApiQuery(fetchUserTakeawayProducts, {
        queryName: TakeawayQuery.TAKEAWAY_USER_PRODUCTS,
        dependencies: { userId: onBehalfUserId ?? 0, webshopId: webshopId ?? 0, date: dateString },
        enabled: Boolean(onBehalfUserId && webshopId),
        keepPreviousData: true,
    })

    const { data, isInitialLoading } = onBehalfUserId ? userProductsQuery : defaultMenuQuery

    const menu = data?.menues?.[0]
    const groups = menu?.productGroups ?? []
    const nonFavGroups = groups.filter((g) => g.id !== FAVORITES_GROUP_ID)
    // Use the favorites group from the menu response — it contains products enriched with
    // current-day data (tags, offerDetails, paymentDetails, etc.) unlike the separate favorites API
    const menuFavGroup = groups.find((g) => g.id === FAVORITES_GROUP_ID)

    // Derive favorited product IDs from menu response (same as mobile: product.userDetails.favoriteId)
    const favoriteIds = useMemo(
        () =>
            new Set(
                groups
                    .flatMap((g) => g.products)
                    .filter((p) => p.userDetails != null && "favoriteId" in p.userDetails)
                    .map((p) => p.id),
            ),
        [groups],
    )

    const { rootRef, activeGroupId, setActiveGroupId, scrollToGroup } = useTakeawayScrollGroups(
        nonFavGroups,
        showFavorites,
    )

    // Store kitchen name from menu API (matches mobile behavior — mobile uses menu response kitchen.name for deliveryLocation)
    useEffect(() => {
        if (data?.kitchen?.name) {
            cartContext?.onMenuKitchenLoaded(data.kitchen.name)
        }
    }, [data?.kitchen?.name])

    const handleGroupSelect = useCallback(
        (groupId: number) => {
            setActiveGroupId(groupId)
            scrollToGroup(groupId)
        },
        [scrollToGroup],
    )

    const handleShowFavoritesChange = useCallback(
        (show: boolean) => {
            setShowFavorites(show)
            if (!show) {
                const first = nonFavGroups[0]
                if (first) {
                    setActiveGroupId(first.id)
                    scrollToGroup(first.id)
                }
            }
        },
        [nonFavGroups, scrollToGroup],
    )

    const searchTrimmed = search.trim()
    const searchRegex = useMemo(
        () => (searchTrimmed ? new RegExp(`(${searchTrimmed})`, "i") : undefined),
        [searchTrimmed],
    )

    const matchesSearch = (name: string) => !searchTrimmed || name.toLowerCase().includes(searchTrimmed.toLowerCase())

    const displayGroups = (() => {
        if (showFavorites) {
            const products = menuFavGroup?.products ?? []
            const filtered = products.filter((p) => matchesSearch(p.name))
            if (!filtered.length) return []
            return [{ id: FAVORITES_GROUP_ID, name: "", products: filtered }]
        }
        if (searchTrimmed) {
            return nonFavGroups
                .map((g) => ({
                    ...g,
                    products: g.products.filter((p) => matchesSearch(p.name)),
                }))
                .filter((g) => g.products.length > 0)
        }
        return nonFavGroups
    })()

    if (isInitialLoading) return <TakeawayFormSkeleton />

    return (
        <Box className={classes.root} ref={rootRef}>
            <TakeawayHero kitchen={kitchen} imageUrl={heroImageUrl} />

            <Box className={classes.stickyHeader}>
                <TakeawayDateBar selectedDate={selectedDate} onDateChange={onDateChange} />
                <TakeawayCategoryBar
                    groups={groups}
                    activeGroupId={activeGroupId}
                    showFavorites={showFavorites}
                    search={search}
                    onGroupChange={handleGroupSelect}
                    onSearchChange={setSearch}
                    onShowFavoritesChange={handleShowFavoritesChange}
                />
            </Box>

            <Box className={classes.content}>
                <TakeawayMenuContent
                    isLoading={isInitialLoading}
                    allGroups={nonFavGroups}
                    displayGroups={displayGroups}
                    showFavorites={showFavorites}
                    favoriteIds={favoriteIds}
                    searchRegex={searchRegex}
                    cart={cart}
                    onQuantityChange={onQuantityChange}
                    onFavoriteToggle={onFavoriteToggle}
                    onDetailsClick={setDetailsProduct}
                />
            </Box>

            {detailsProduct && detailsUserId && (
                <ProductDetailsDialog
                    userId={detailsUserId}
                    productId={detailsProduct.id}
                    isFavorite={favoriteIds.has(detailsProduct.id)}
                    onFavoriteToggle={(_id, makeFavorite) => onFavoriteToggle(detailsProduct, makeFavorite)}
                    handleClose={() => setDetailsProduct(null)}
                />
            )}
        </Box>
    )
}
